// src/lib/relationship-service.ts
import {
  collection, doc, getDocs, query, where, setDoc, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { db } from './firebase'; // confirm the app's firestore export path
import {
  EntityRelationship, RelationshipType, RelationshipSource, Confidence, ReviewStatus,
  effectiveConfidence,
} from '@/types/relationships';
// The controlled 9-type vocabulary is single-sourced in shared/graph/ontology.js
// (consolidation U11) and consumed by both runtimes. Derive VALID_TYPES from it rather
// than re-listing the enum here.
import { getValidTypes } from '@shared/graph/ontology';

const COLLECTION = 'entity_relationships';
const VALID_TYPES = getValidTypes() as RelationshipType[];

export interface EdgeInput {
  fromId: string;                // immutable Tag doc id (join key)
  toId: string;
  fromSlug: string;              // denormalized for display
  toSlug: string;
  fromName: string;              // denormalized display name (kills frontend join; rename hook refreshes)
  toName: string;
  fromType?: string | null;      // denormalized source entityType (for 'in'-direction rendering)
  toType?: string | null;        // denormalized target entityType
  type: RelationshipType;
  role?: string;
  description?: string;          // optional narrative ("named after his dog")
  startDate?: string | null;
  endDate?: string | null;
  status?: ReviewStatus;         // default 'confirmed' for human admin writes
  confidence?: Confidence;       // optional human override; else computed from sources
  sources: RelationshipSource[]; // ≥1 evidence source (corroboration array)
  sponsored?: boolean;
}

/** Validate before write. Enforces the controlled enum + ≥1 source + immutable ids. */
export function validateEdgeInput(input: EdgeInput): string[] {
  const errors: string[] = [];
  if (!input.fromId) errors.push('fromId required');
  if (!input.toId) errors.push('toId required');
  if (input.fromId === input.toId) errors.push('self-edge not allowed');
  if (!VALID_TYPES.includes(input.type)) errors.push(`type must be one of ${VALID_TYPES.join('/')}`);
  // ASSOCIATED_WITH is deliberately general — the mandatory description is what keeps it
  // from becoming a junk-drawer: every edge must say, in prose, what the association IS.
  if (input.type === 'ASSOCIATED_WITH' && !input.description?.trim())
    errors.push('ASSOCIATED_WITH requires a description (the narrative is the edge)');
  if (!input.sources?.length) errors.push('at least one source required');
  if (input.sources?.some(s => !s.verificationMethod)) errors.push('every source needs a verificationMethod');
  // Reject unparseable dates so they can never be written and then silently vanish from asOf queries.
  const parseable = (d?: string | null) => d == null || d === '' || !Number.isNaN(new Date(d).getTime());
  if (!parseable(input.startDate)) errors.push('startDate is not a valid date');
  if (!parseable(input.endDate)) errors.push('endDate is not a valid date');
  // Compare PARSED timestamps (lexical compare mis-flags coarse vs full dates, e.g. '2021' vs '2021-04-01').
  if (input.endDate && input.startDate &&
      !Number.isNaN(new Date(input.endDate).getTime()) && !Number.isNaN(new Date(input.startDate).getTime()) &&
      new Date(input.endDate).getTime() < new Date(input.startDate).getTime()) {
    errors.push('endDate before startDate');
  }
  return errors;
}

/**
 * Collision-free, charset-safe Firestore doc id derived from the natural upsert key.
 * base64url(utf8(key)) is INJECTIVE — distinct keys never collide (unlike the old lossy
 * `replace(/[^a-zA-Z0-9_-]/g,'-')`, where 'luca.eth' and 'luca-eth' mapped to one id, and the
 * '__' delimiter could be forged by a segment containing '__'). The 'rel_' prefix guarantees the
 * id never matches Firestore's reserved `__.*__` pattern. Same logical edge → same id (idempotent upsert).
 */
export function edgeDocId(
  input: Pick<EdgeInput, 'fromId' | 'toId' | 'type' | 'startDate'>
): string {
  // Per-segment encode + a delimiter ('/') that cannot appear in any encoded segment (encodeURIComponent
  // escapes '/' → %2F) makes the pre-image INJECTIVE: 'a__b'|'c' and 'a'|'b__c' (and 'luca.eth' vs
  // 'luca-eth') can never collapse to one string. base64url of an injective pre-image is collision-free.
  const raw = [input.fromId, input.toId, input.type, input.startDate ?? 'null']
    .map(encodeURIComponent).join('/');
  const b64 = btoa(raw).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return `rel_${b64}`;
}

/** Deterministic doc id from the dedup key (ids + startDate → preserves history). */
export function buildEdgeDoc(input: EdgeInput): { id: string; data: Omit<EntityRelationship, 'id'> } {
  const id = edgeDocId(input);
  const latest = input.sources.reduce((a, b) => (a.observedAt > b.observedAt ? a : b));
  return {
    id,
    data: {
      fromId: input.fromId,
      toId: input.toId,
      fromSlug: input.fromSlug,
      toSlug: input.toSlug,
      fromName: input.fromName,
      toName: input.toName,
      fromType: input.fromType ?? null,
      toType: input.toType ?? null,
      type: input.type,
      role: input.role,
      description: input.description,
      startDate: input.startDate ?? null,
      endDate: input.endDate ?? null,
      status: input.status ?? 'confirmed',
      sources: input.sources,
      confidence: input.confidence,            // optional override; else computed at read
      lastVerifiedAt: latest.observedAt,
      sponsored: !!input.sponsored,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  };
}

/** READ: all edges touching an entity id (out + in). Used by admin UI to list/edit. */
export async function getEdgesForEntity(entityId: string): Promise<EntityRelationship[]> {
  if (!db) return [];
  const col = collection(db, COLLECTION);
  const [outSnap, inSnap] = await Promise.all([
    getDocs(query(col, where('fromId', '==', entityId))),
    getDocs(query(col, where('toId', '==', entityId))),
  ]);
  const map = new Map<string, EntityRelationship>();
  for (const d of [...outSnap.docs, ...inSnap.docs]) {
    map.set(d.id, { id: d.id, ...(d.data() as Omit<EntityRelationship, 'id'>) });
  }
  return [...map.values()];
}

/** Resolve a target slug → its immutable {id, slug, name, type} so edges store stable id + denormalized name. */
export async function resolveEntityRefBySlug(
  slug: string
): Promise<{ id: string; slug: string; name: string; type: string | null } | null> {
  if (!db) return null;
  const snap = await getDocs(query(collection(db, 'tags'), where('slug', '==', slug)));
  if (snap.empty) return null;
  const d = snap.docs[0];
  const data = d.data() as { slug?: string; name?: string; entityType?: string };
  return { id: d.id, slug: data.slug || slug, name: data.name || slug, type: data.entityType || null };
}

/**
 * RENAME HOOK (data-integrity): when an entity's slug/name/type changes, rewrite the denormalized
 * fields on every edge it touches. Call from the tag-update flow (update-glossary-entity / updateTag).
 * Without this, denormalized fromSlug/toSlug/fromName/toName/toType drift after a rename.
 */
export async function propagateEntityRename(
  entityId: string, next: { slug: string; name: string; type?: string | null }
): Promise<number> {
  if (!db) return 0;
  const col = collection(db, COLLECTION);
  const [outSnap, inSnap] = await Promise.all([
    getDocs(query(col, where('fromId', '==', entityId))),
    getDocs(query(col, where('toId', '==', entityId))),
  ]);
  // The FROM entity's rename refreshes fromSlug/fromName/fromType; the TO entity's, toSlug/toName/toType.
  const patches = [
    ...outSnap.docs.map(d => ({ ref: d.ref, patch: { fromSlug: next.slug, fromName: next.name, fromType: next.type ?? null } })),
    ...inSnap.docs.map(d => ({ ref: d.ref, patch: { toSlug: next.slug, toName: next.name, toType: next.type ?? null } })),
  ];
  // Batched in chunks of ≤500 (Firestore's per-batch limit) → atomic-per-chunk, fewer round-trips,
  // and (being idempotent) safely retryable. Replaces the old N sequential non-atomic setDoc calls.
  for (let i = 0; i < patches.length; i += 500) {
    const batch = writeBatch(db);
    for (const { ref, patch } of patches.slice(i, i + 500)) {
      batch.set(ref, { ...patch, updatedAt: serverTimestamp() }, { merge: true });
    }
    await batch.commit();
  }
  return patches.length;
}

/**
 * DELETE HOOK (data-integrity): when an entity is deleted, remove every edge it touches so the graph
 * doesn't accumulate dangling docs (the read path tolerates dangling targets, but storage shouldn't
 * grow unboundedly). Call from the tag-delete flow. Batched ≤500, deduped by doc id, non-blocking.
 */
export async function deleteEntityEdges(entityId: string): Promise<number> {
  if (!db) return 0;
  const col = collection(db, COLLECTION);
  const [outSnap, inSnap] = await Promise.all([
    getDocs(query(col, where('fromId', '==', entityId))),
    getDocs(query(col, where('toId', '==', entityId))),
  ]);
  const byId = new Map<string, ReturnType<typeof doc>>();
  for (const d of [...outSnap.docs, ...inSnap.docs]) byId.set(d.id, d.ref); // dedup (a self-edge appears twice)
  const refs = [...byId.values()];
  for (let i = 0; i < refs.length; i += 500) {
    const batch = writeBatch(db);
    for (const ref of refs.slice(i, i + 500)) batch.delete(ref);
    await batch.commit();
  }
  return refs.length;
}

/** WRITE (GATED): upsert one edge by dedup key. Caller must validate first. */
export async function upsertEdge(input: EdgeInput): Promise<string> {
  if (!db) throw new Error('Firebase not initialized');
  const errors = validateEdgeInput(input);
  if (errors.length) throw new Error(`Invalid edge: ${errors.join('; ')}`);
  const { id, data } = buildEdgeDoc(input);
  await setDoc(doc(db, COLLECTION, id), { ...data, updatedAt: serverTimestamp() }, { merge: true });
  return id;
}

export { effectiveConfidence };
